import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, EmptyState, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { CustomReportView, ReportCatalogView } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { REPORT_SOURCE_LABELS } from "@cx/domain";
import { useSession } from "../../session";
import { errorMessage } from "../../errors";
import { dateTimeBR } from "../invoices/InvoiceEvents";

/**
 * Meu Negócio › Relatórios (T3.6, RF-CAD-06): modelos salvos (os próprios e os compartilhados) e modelos prontos do
 * construtor (vendas por período/cliente/produto, estoque valorizado, contas por vencimento, comissões).
 */
export function CustomReportsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [archive, setArchive] = useState<CustomReportView | null>(null);
  const list = useQuery({
    queryKey: ["custom-reports", companyId],
    queryFn: () => api<{ data: CustomReportView[] }>("/custom-reports"),
  });
  const catalog = useQuery({
    queryKey: ["custom-reports", companyId, "catalog"],
    queryFn: () => api<ReportCatalogView>("/custom-reports/catalog"),
  });
  const doArchive = useMutation({
    mutationFn: (id: string) => api(`/custom-reports/${id}/archive`, { method: "POST" }),
    onSuccess: () => {
      toast("success", "Relatório arquivado");
      qc.invalidateQueries({ queryKey: ["custom-reports", companyId] });
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const rows = list.data?.data ?? [];
  const canCreate = can(perms, "custom_reports", "create");
  return (
    <>
      <ContentHeader
        title="Relatórios"
        breadcrumb={["Meu Negócio", "Relatórios"]}
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => nav({ to: "/meu-negocio/relatorios/novo" })}
          >
            Novo relatório
          </button>
        }
      />
      <div className="row g-3">
        <div className="col-lg-8">
          <div className="box card">
            <h2 className="h6 p-3 pb-0 mb-0">Relatórios salvos</h2>
            <QueryStatus loading={list.isLoading} error={list.error} />
            {list.isSuccess && rows.length === 0 && (
              <EmptyState
                title="Nenhum relatório salvo"
                description="Monte um relatório escolhendo a fonte, as colunas, os filtros e os resumos, ou comece por um modelo pronto."
                actions={[
                  { label: "Novo relatório", onClick: () => nav({ to: "/meu-negocio/relatorios/novo" }) },
                ]}
              />
            )}
            {rows.length > 0 && (
              <div className="table-responsive">
                <table className="table align-middle mb-0" aria-label="Relatórios salvos">
                  <thead>
                    <tr>
                      <th scope="col">Nome</th>
                      <th scope="col">Fonte</th>
                      <th scope="col">Visibilidade</th>
                      <th scope="col">Atualizado em</th>
                      <th scope="col" className="text-end">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <Link to="/meu-negocio/relatorios/$id" params={{ id: r.id }}>
                            {r.name}
                          </Link>
                          {r.description && <div className="small text-muted">{r.description}</div>}
                        </td>
                        <td>{REPORT_SOURCE_LABELS[r.source]}</td>
                        <td>
                          <StatusBadge tone={r.shared ? "info" : "secondary"}>
                            {r.shared ? "Compartilhado" : "Só eu"}
                          </StatusBadge>
                        </td>
                        <td>{dateTimeBR(r.updatedAt)}</td>
                        <td className="text-end">
                          {r.canEdit && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => setArchive(r)}
                            >
                              Arquivar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        <div className="col-lg-4">
          <div className="box card p-3">
            <h2 className="h6">Modelos prontos</h2>
            <QueryStatus loading={catalog.isLoading} error={catalog.error} />
            <ul className="list-unstyled mb-0">
              {(catalog.data?.presets ?? []).map((p) => (
                <li key={p.id} className="mb-2">
                  <Link to="/meu-negocio/relatorios/novo" search={{ modelo: p.id }}>
                    {p.name}
                  </Link>
                  <div className="small text-muted">{p.description}</div>
                </li>
              ))}
            </ul>
            {!canCreate && (
              <p className="small text-muted mt-2 mb-0">
                Seu perfil roda e exporta, mas não salva relatórios.
              </p>
            )}
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={archive !== null}
        title="Arquivar relatório"
        message={`Arquivar "${archive?.name ?? ""}"? Ele sai da lista; o histórico fica na auditoria.`}
        confirmLabel="Arquivar"
        onConfirm={() => {
          if (archive) doArchive.mutate(archive.id);
          setArchive(null);
        }}
        onCancel={() => setArchive(null)}
      />
    </>
  );
}
